import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  ImageBackground,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import {
  BlueButton,
  ActiveTablesBtn,
  EntryInfoLight,
  MainUsersLiveFramePng,
  ProfileChipPng,
  TournamentsChipImg,
  WinButtonCoins,
} from "../../../assets";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../navigator";
import { useDispatch, useSelector } from "react-redux";
import {
  tournamentsActiveRequest,
  tournamentsTableEnter,
} from "../../../store/tournaments/tournamentsActions";
import { RootState } from "../../../store";
import { getSocket } from "../../../services/socket";
import { TournamentProps } from "../Middle/TournamentsSection";
import { useTournamentRegistration } from "./useTournamentRegistration";
import { useTranslation } from "react-i18next";
import { useDevice } from "../../../utils/hooks/useDevice";

type Nav = NativeStackNavigationProp<RootStackParamList>;

const Tournaments = ({
  sng,
  trophySource,
  winButtonSource,
  itemWidth,
  isSpecial,
}: TournamentProps & { itemWidth: number; isSpecial?: boolean }) => {
  const { navigate } = useNavigation<Nav>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { width: screenW } = useWindowDimensions();

  const tournamentId = String(sng?.id ?? "");
  const isWide = screenW >= 980;
  const isMid = screenW >= 769 && screenW <= 1250;
  const isSmall = screenW < 768;

  const roomId =
    (sng as any).room_id ??
    (sng as any).roomId ??
    (sng as any).room ??
    (sng as any).table ??
    (sng as any).tableId;

  const { isSigned, handleRegisterPress } = useTournamentRegistration({
    tournamentId,
    roomId: roomId != null ? String(roomId) : undefined,
    sng,
  });

  const targetTime = (sng as any)?.close_at ?? (sng as any)?.start_in;
  const hasRefetchedRef = useRef(false);
  const closeAt = (sng as any)?.close_at;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!closeAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [closeAt]);
  const isExpired =
    closeAt != null && new Date(closeAt).getTime() <= Date.now();

  const auth = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (!targetTime) return;
    const interval = setInterval(() => {
      const future = new Date(targetTime).getTime();
      const now = Date.now();
      const diff = Math.max(future - now, 0);
      if (diff <= 0 && !hasRefetchedRef.current) {
        hasRefetchedRef.current = true;
        const socket = auth?.user?.id && auth?.user?.username
          ? getSocket(String(auth.user.id), String(auth.user.username))
          : null;
        if (socket) {
          socket.emit("tournament:getSnGTournaments", {});
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [targetTime, auth?.user?.id, auth?.user?.username]);

  if (!roomId) {
    console.warn("❌ Missing roomId for tournament", tournamentId, sng);
    return null;
  }

  const ui = useMemo(() => {
    const contentW = Math.max(260, Math.round(itemWidth)); // safety clamp

    const trophyRatio = isSpecial
      ? isWide
        ? 0.64
        : 0.74
      : isWide
        ? 0.58
        : 0.62;
    const trophySize = Math.round(contentW * trophyRatio);

    const winW = Math.round(contentW * (isWide ? 0.7 : 0.7));

    const winH = Math.round(isWide ? winW * 0.28 : winW * 0.28);
    const buttonW = Math.round(contentW * (isWide ? 0.3 : 0.36));
    const activeTablesBtn = Math.round(contentW * (isWide ? 0.3 : 0.4));

    return {
      contentW,
      trophySize,
      winW,
      winH,
      buttonW,
      activeTablesBtn,
      buttonH: Math.round(buttonW * 0.28),
      font: {
        win: isMid ? 16 : isSmall ? 22 : 20,
        amount: isMid ? 16 : isSmall ? 22 : 20,
        entry: isWide ? 15 : 14,
        register: isMid ? 12 : isSmall ? 16 : 14,
      },
    };
  }, [itemWidth, isWide, isSpecial, isMid]);

  const WinSection = (
    <ImageBackground
      source={winButtonSource}
      resizeMode="stretch"
      style={[
        styles.winButtonBg,
        {
          width: isWide ? ui.winW : ui.winW,
          height: isWide ? ui.winH : ui.winH,
          marginTop: isWide ? 0 : -45,
          marginBottom: isWide ? 10 : 0,
        },
      ]}
      imageStyle={styles.winBgImg}
    >
      <View style={styles.winContent}>
        <Text style={[styles.winLabel, { fontSize: ui.font.win }]}>
          {t("T_SITE_TOURNAMENT_WIN")}
        </Text>
        <Image source={WinButtonCoins} style={styles.winCoins} />
        <Text style={[styles.winAmount, { fontSize: ui.font.amount }]}>
          {sng.winAmount}
        </Text>
      </View>
    </ImageBackground>
  );

  const TrophySection = (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={() => {
        dispatch(tournamentsTableEnter({ table: tournamentId }));
        navigate("Tournament", { tournamentId, roomId: String(roomId) });
      }}
      style={[
        styles.trophyWrap,
        {
          width: ui.trophySize,
          height: ui.trophySize,
          borderRadius: ui.trophySize / 2,
        },
      ]}
    >
      <ImageBackground
        source={trophySource}
        resizeMode="contain"
        style={{ width: "100%", height: "100%" }}
      />
      <View style={styles.participantsWrap}>
        <ImageBackground
          source={MainUsersLiveFramePng}
          style={[
            styles.badgeStyle,
            { width: isWide ? 66 : 66, height: isWide ? 66 : 66 },
          ]}
          resizeMode="contain"
        >
          <Text
            style={[
              styles.badgeText,
              { fontSize: isWide ? 12 : 14, marginTop: isWide ? 12 : 10 },
            ]}
          >
            {Array.isArray(sng?.users) ? sng.users.length : 0}/8
          </Text>
        </ImageBackground>
      </View>
    </TouchableOpacity>
  );

  return (
    <View
      style={[styles.container, { width: ui.contentW, gap: isWide ? 8 : 4 }]}
    >
      {isWide ? (
        <View style={{ marginTop: isSpecial ? -30 : 0, alignItems: "center" }}>
          {WinSection}
          {TrophySection}
        </View>
      ) : (
        <>
          {TrophySection}
          {WinSection}
        </>
      )}

      <View style={styles.entryContainer}>
        <Image source={EntryInfoLight} style={styles.entryDivider} />
        <View style={styles.entryRow}>
          <Text style={[styles.entryLabel, { fontSize: ui.font.entry }]}>
            {t("T_SITE_TOURNAMENT_PLAYERS_ENTRY")}
          </Text>
          <Image source={TournamentsChipImg} style={styles.entryChip} />
          <Text style={[styles.entryValue, { fontSize: ui.font.entry }]}>
            {sng?.entry_size}
          </Text>
        </View>
        <Image source={EntryInfoLight} style={styles.entryDivider} />
      </View>

      <View>
        <TouchableOpacity
          activeOpacity={0.8}
          onPress={() => {
            dispatch(tournamentsActiveRequest({ roomId: String(roomId) }));
            navigate("ActiveTournament", { roomId: String(roomId) });
          }}
        >
          <ImageBackground
            source={ActiveTablesBtn}
            resizeMode="stretch"
            style={[
              styles.activeTablesBtn,
              {
                width: isWide ? 140 : ui.activeTablesBtn,
                marginTop: isWide ? 4 : 10,
                height: isWide ? 40 : ui.buttonH + 10,
              },
            ]}
          >
            <View
              style={[
                styles.stakeWrap,
                {
                  paddingVertical: isWide ? 2 : 0,
                },
              ]}
            >
              <Text
                style={[
                  styles.stakeLabel,
                  {
                    fontSize: isWide ? 12 : 14,
                    paddingHorizontal: isWide ? 0 : 2,
                  },
                ]}
              >
                {t("T_SITE_TOURNAMENT_ACTIVE_ROOMS")}
              </Text>

              <Text style={[styles.stakeValue, { fontSize: isWide ? 14 : 14 }]}>
                {sng?.active_tables}
              </Text>
            </View>
          </ImageBackground>
        </TouchableOpacity>
      </View>

      <TouchableOpacity
        activeOpacity={0.9}
        onPress={isExpired ? undefined : handleRegisterPress}
        disabled={isExpired}
        style={{
          marginTop: isWide ? 8 : 14,
          alignSelf: "center",
          opacity: isExpired ? 0.7 : 1,
        }}
      >
        <ImageBackground
          source={BlueButton}
          resizeMode="stretch"
          style={{
            minHeight: isWide ? 30 : 32,
            paddingHorizontal: isWide ? 18 : 20,
            paddingVertical: 6,
            alignItems: "center",
            justifyContent: "center",
            alignSelf: "center",
          }}
        >
          <View style={styles.registerCenter}>
            <Text
              style={[
                styles.registerText,
                {
                  fontSize: ui.font.register,
                  textAlign: "center",
                  maxWidth: isWide ? ui.contentW * 0.7 : ui.contentW * 0.8,
                },
              ]}
            >
              {isExpired || !isSigned
                ? t("T_SITE_TOURNAMENT_REGISTER")
                : t("T_SITE_TOURNAMENT_UNREGISTER")}
            </Text>
          </View>
        </ImageBackground>
      </TouchableOpacity>
    </View>
  );
};

export default Tournaments;

const styles = StyleSheet.create({
  container: {
    alignSelf: "center",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
  },

  winButtonBg: { justifyContent: "center", alignItems: "center" },
  winBgImg: { borderRadius: 20 },
  winContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  winLabel: {
    fontWeight: "700",
    color: "#fff",
    textShadowColor: "rgba(0,0,0,0.7)",
    textShadowOffset: { width: 1, height: 2 },
    textShadowRadius: 3,
  },
  winCoins: { width: 44, height: 34, marginHorizontal: 12 },
  winAmount: {
    fontWeight: "700",
    color: "#fff",
    textShadowColor: "rgba(0,0,0,0.7)",
    textShadowOffset: { width: 1, height: 2 },
    textShadowRadius: 3,
  },
  stakeWrap: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 0,
    paddingVertical: 6,
    borderRadius: 20,
  },
  stakeLabel: {
    color: "#ecdb79",
    fontWeight: "bold",
    fontSize: 16,
    marginRight: 6,
    textTransform: "uppercase",

    paddingVertical: 6,
  },
  stakeChip: {
    width: 24,
    height: 24,
    borderRadius: 8,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
  stakeValue: {
    fontWeight: "bold",
    color: "#ecdb79",
    fontSize: 14,
  },

  activeTablesBtn: {
    height: 40,
    justifyContent: "center",
    alignItems: "center",
  },

  trophyWrap: {
    borderWidth: 2,
    borderColor: "#F0B223",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  participantsWrap: {
    position: "absolute",
    right: "-15%",
    top: "50%",
    marginTop: -35,
  },
  badgeStyle: { justifyContent: "center", alignItems: "center" },
  badgeText: { color: "#ffe593", fontWeight: "700", textAlign: "center" },

  entryContainer: { alignItems: "center", gap: 6 },
  entryDivider: { width: 180, height: 6, resizeMode: "stretch" },
  entryRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  entryLabel: {
    color: "#ffffff",
    fontWeight: "700",
    textShadowColor: "rgba(0,0,0,0.7)",
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 2,
    textTransform: "uppercase",
  },
  entryChip: { width: 26, height: 26 },
  entryValue: { color: "#ffffff", fontWeight: "700" },

  registerCenter: { flex: 1, alignItems: "center", justifyContent: "center" },
  registerText: {
    fontWeight: "700",
    color: "#fff",
    textShadowColor: "#3c3737ff",
    textShadowOffset: { width: 1, height: 2 },
    textShadowRadius: 2,
    letterSpacing: 1,
  },
});
